<div class="flex max-h-[calc(100vh-330px)] mt-8" [class.overflow-hidden]="!useRouting()">
    @if (hasExcessiveProducts()) {
        <!-- Warning message for excessive products -->
        <div class="flex-1 flex items-center justify-center p-8">
            <div
                class="max-w-2xl w-full bg-light-bgColorCard dark:bg-dark-bgColorCard rounded-lg shadow-lg p-8 border-l-4 border-yellow-500"
            >
                <div class="flex items-start gap-4">
                    <div class="flex-1">
                        <h2 class="text-2xl font-bold mb-4 text-light-textColor dark:text-dark-textColor">
                            {{ 'workflow.excessive-products.title' | translate }}
                        </h2>
                        <p class="text-lg mb-6 text-light-textColor dark:text-dark-textColor">
                            {{
                                'workflow.excessive-products.message'
                                    | translate: { count: workflow()?.totalProductCount || 0 }
                            }}
                        </p>
                        @if (transitionPhase() !== WorkflowTransitionPhase.IDLE) {
                            <app-workflow-transition-stepper class="block mb-6" [phase]="transitionPhase()" />
                        }
                        <div class="flex gap-4">
                            <sag-ui-button
                                [icon]="isLoadingExcelDownload() ? undefined : 'download'"
                                [ariaLabel]="'workflow.excessive-products.download-cta' | translate"
                                (click)="downloadExcelForWorkflow()"
                                color="primary"
                                [disabled]="isLoadingExcelDownload() || isLoadingMoveToNextStep()"
                            >
                                @if (isLoadingExcelDownload()) {
                                    <span class="inline-flex items-center">
                                        <app-loading-indicator></app-loading-indicator>
                                        <span>{{ 'general.loading' | translate }}</span>
                                    </span>
                                } @else {
                                    {{ 'workflow.excessive-products.download-cta' | translate }}
                                }
                            </sag-ui-button>
                            <sag-ui-button
                                [icon]="isLoadingMoveToNextStep() ? undefined : 'chevron-right'"
                                [ariaLabel]="'workflow.excessive-products.move-to-next-step' | translate"
                                (click)="moveExcessiveWorkflowToNextStep()"
                                color="primary"
                                [disabled]="isLoadingMoveToNextStep() || isLoadingExcelDownload()"
                            >
                                @if (isLoadingMoveToNextStep()) {
                                    <span class="inline-flex items-center">
                                        <app-loading-indicator></app-loading-indicator>
                                        <span>{{ 'general.loading' | translate }}</span>
                                    </span>
                                } @else {
                                    {{ 'workflow.excessive-products.move-to-next-step' | translate }}
                                }
                            </sag-ui-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    } @else {
        <div class="w-80 p-6 sticky min-h-[30vh] overflow-auto">
            @if (this.isWorkflowConfigLoadingAndExisting() || isTransitioning()) {
                <app-workflow-stepper-skeleton></app-workflow-stepper-skeleton>
            } @else {
                <sag-ui-stepper
                    [steps]="steps()"
                    [active]="activeStep()"
                    [activeSubStep]="activeSection()"
                    (selectedChange)="setStep($event)"
                    (selectedSubStepChange)="setSection($event)"
                    [invalidSteps]="invalidSteps()"
                    orientation="vertical"
                    [styleConfig]="stepperConfig"
                >
                </sag-ui-stepper>
            }
        </div>
        <div class="flex flex-col ml-8 flex-1">
            @if (transitionPhase() !== WorkflowTransitionPhase.IDLE) {
                <div class="px-6 pt-4">
                    <div class="max-w-3xl mx-auto">
                        <app-workflow-transition-stepper [phase]="transitionPhase()" />
                    </div>
                </div>
            }
            @if (this.isWorkflowConfigLoadingAndExisting() || !isWorkflowFormReady() || isTransitioning()) {
                <app-workflow-content-skeleton></app-workflow-content-skeleton>
            } @else {
                <div class="flex-1 overflow-auto relative">
                    @for (step of filteredWorkflowConfigSteps(); track step.name; let stepIndex = $index) {
                        <!-- If step has sections, render only the sections -->
                        @if (!!step.sections && step.sections.length > 0) {
                            <div class="flex flex-col gap-4 mt-4">
                                @for (section of step.sections; track section.name; let sectionIndex = $index) {
                                    <!-- If useRouting is false we are in the creation phase and we show only relevant steps for the creation, otherwise all -->
                                    @if (useRouting() || section.shownInCreate) {
                                        <div #stepRef [id]="'step-' + stepIndex" class="mb-8 p-6">
                                            <div class="max-w-3xl mx-auto">
                                                <h3 class="text-2xl font-bold mb-6">{{ step.name | translate }}</h3>
                                            </div>
                                            <div
                                                #sectionRef
                                                [id]="'section-' + stepIndex + '-' + sectionIndex"
                                                class="mb-8"
                                            >
                                                <div class="min-h-[70vh] mb-12 pt-4 flex flex-col gap-4">
                                                    <div class="flex flex-col gap-4">
                                                        <div class="max-w-3xl mx-auto w-full">
                                                            <h4 class="text-2xl font-bold mb-6">
                                                                {{ section.name | translate }}
                                                            </h4>
                                                        </div>
                                                        <app-workflow-step-fields
                                                            [stepConfig]="section"
                                                            [disabled]="!isActiveStep(step.name)"
                                                            [stepForm]="
                                                                step.name
                                                                    | getSectionForm: section.name : workflowFormGroup()
                                                            "
                                                            [productsForm]="productsForm()"
                                                            [isSection]="true"
                                                            [isCreate]="!useRouting()"
                                                        >
                                                        </app-workflow-step-fields>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                }
                            </div>
                        } @else {
                            <!-- If useRouting is false we are in the creation phase and we show only relevant steps for the creation, otherwise all -->
                            @if (step.fields && step.fields.length > 0 && (useRouting() || step.shownInCreate)) {
                                <div #stepRef [id]="'step-' + stepIndex" class="mb-8 p-6">
                                    <div class="min-h-[70vh] mb-12 pt-4 flex flex-col gap-4">
                                        <div class="flex flex-col gap-4">
                                            <div class="max-w-3xl mx-auto w-full">
                                                <h3 class="text-2xl font-bold mb-6">{{ step.name | translate }}</h3>
                                            </div>
                                            <app-workflow-step-fields
                                                [stepConfig]="step"
                                                [stepForm]="step.name | getStepForm: workflowFormGroup()"
                                                [productsForm]="productsForm()"
                                                [isSection]="false"
                                                [isCreate]="!useRouting()"
                                            >
                                            </app-workflow-step-fields>
                                        </div>
                                    </div>
                                </div>
                            }
                        }
                    } @empty {
                        <div
                            class="flex flex-col gap-4 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        >
                            <h3 class="text-2xl font-bold mb-6">
                                {{ 'workflow.workflow-tab.no-steps-available' | translate }}
                            </h3>
                            <p class="text-gray-500">{{ 'workflow.workflow-tab.check-config' | translate }}</p>
                        </div>
                    }
                </div>
                <div class="flex-shrink-0 sticky bottom-0 left-0 right-0 p-4 z-[60]" [class]="bgColor()">
                    <div class="max-w-3xl mx-auto flex flex-col gap-4">
                        <div class="flex gap-4 items-center justify-end">
                            <sag-ui-button
                                (click)="activeSection() !== -1 ? goToPreviousSection() : goToPrevious()"
                                icon="close"
                                color="tertiary"
                                [disabled]="isFirstStep() || isTransitioning()"
                            >
                                {{ 'workflow.buttons.previous' | translate }}
                            </sag-ui-button>

                            <sag-ui-button
                                [disabled]="
                                    (isLastStep() && isWorkflowFormDisabled() && !isAdmin()) || isTransitioning()
                                "
                                (click)="activeSection() !== -1 ? goToNextSection() : goToNext()"
                                icon="chevron-right"
                            >
                                {{
                                    isLastStep()
                                        ? ('workflow.buttons.complete' | translate)
                                        : ('workflow.buttons.next' | translate)
                                }}
                            </sag-ui-button>
                        </div>
                    </div>
                </div>
            }
        </div>
    }
</div>
